<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable
      ref="rtable"
      :settings="{ 
        hideTable: true, 
        hideCards: false,
        hideExport: true,
        title: t('batchTrackerReport'), 
        filterRequired: true, 
        periodRequired: true,
        customRowsKey: null,
        tableType: null,
        theme: null,
        dateRangeFormat: null,
        locationRequired: false,
        appendQueryParams: {}
      }"
      endpoint="reports/inventory/item-batch"
      @onDataLoaded="onDataLoaded"
    >
      <template #filter-bottom>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-2 flex-wrap w-full">
            <el-select
              filterable
              clearable
              :placeholder="t('selectItemPlaceholder')"
              v-model="selectedItem"
              @change="onSelectedItemChanged"
              value-key="id"
              remote
              :remote-method="searchItems"
              class="min-w-[250px]"
            >
              <el-option
                v-for="option in items"
                :key="option.id"
                :label="option.name"
                :value="option"
              ></el-option>
            </el-select>

            <el-select
              filterable
              clearable
              :placeholder="t('selectBatchPlaceholder')"
              v-model="selectedBatch"
              @change="onSelectedBatchChanged"
              value-key="id"
              :disabled="!selectedItem"
              class="min-w-[250px]"
            >
              <el-option
                v-for="option in batchNumbers"
                :key="option.id"
                :label="`${option.batchNumber} - ${formatExpiryDate(option.expiryDate)}`"
                :value="option"
              ></el-option>
            </el-select>

            <el-select
              filterable
              clearable
              :placeholder="t('selectCustomerPlaceholder')"
              v-model="selectedCustomer"
              @change="onSelectedCustomerChanged"
              value-key="id"
              remote
              :remote-method="searchCustomers"
              class="min-w-[250px]"
            >
              <el-option
                v-for="option in customers"
                :key="option.id"
                :label="option.name"
                :value="option"
              ></el-option>
            </el-select>

            <el-select
              filterable
              clearable
              :placeholder="t('selectCashierPlaceholder')"
              v-model="selectedCashier"
              @change="onSelectedCashierChanged"
              value-key="id"
              remote
              :remote-method="searchCashiers"
              class="min-w-[250px]"
            >
              <el-option
                v-for="option in cashiers"
                :key="option.id"
                :label="option.name"
                :value="option"
              ></el-option>
            </el-select>

            <el-select
              filterable
              clearable
              :placeholder="t('selectSalesRepPlaceholder')"
              v-model="selectedSalesRep"
              @change="onSelectedSalesRepChanged"
              value-key="id"
              remote
              :remote-method="searchSalesReps"
              class="min-w-[250px]"
            >
              <el-option
                v-for="option in salesReps"
                :key="option.id"
                :label="option.name"
                :value="option"
              ></el-option>
            </el-select>

            <el-select
              filterable
              clearable
              :placeholder="t('selectCityPlaceholder')"
              v-model="selectedCity"
              @change="onSelectedCityChanged"
              value-key="value"
              remote
              :remote-method="searchCities"
              class="min-w-[250px]"
            >
              <el-option
                v-for="option in cities"
                :key="option.value"
                :label="option.value"
                :value="option.value"
              ></el-option>
            </el-select>
        </div>
      </template>
      
      <template #cards>
        <div v-if="selectedBatch && data" class="space-y-6">
          <!-- Summary Cards -->
          <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div class="bg-white rounded-md py-4 px-6 shadow-sm border">
              <div class="text-gray-500 text-sm">{{ t('currentStock') }}</div>
              <div class="text-2xl font-bold">{{ data?.currentStock || 0 }}</div>
            </div>
            <div class="bg-white rounded-md py-4 px-6 shadow-sm border">
              <div class="text-gray-500 text-sm">{{ t('totalSold') }}</div>
              <div class="text-2xl font-bold">{{ data?.totalSold || 0 }}</div>
            </div>
            <div class="bg-white rounded-md py-4 px-6 shadow-sm border">
              <div class="text-gray-500 text-sm">{{ t('averageCost') }}</div>
              <div class="text-2xl font-bold">{{ formatMoney(data?.averageCost, $ctx.currency) }}</div>
            </div>
            <div class="bg-white rounded-md py-4 px-6 shadow-sm border">
              <div class="text-gray-500 text-sm">{{ t('averageSalePrice') }}</div>
              <div class="text-2xl font-bold">{{ formatMoney(data?.averageSellPrice, $ctx.currency) }}</div>
            </div>
            <div class="bg-white rounded-md py-4 px-6 shadow-sm border">
              <div class="text-gray-500 text-sm">{{ t('totalSales') }}</div>
              <div class="text-2xl font-bold">{{ formatMoney(data?.totalSales, $ctx.currency) }}</div>
            </div>
          </div>

          <!-- Quantity In Hand Table -->
          <div v-if="data?.qtyInHand && data.qtyInHand.length > 0" class="bg-white rounded-md shadow-sm border overflow-hidden">
            <div class="px-4 py-3 border-b cursor-pointer hover:bg-gray-50" @click="toggleSection('qtyInHand')">
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-medium text-gray-900">{{ t('qtyInHand') }}</h3>
                <el-icon class="text-gray-500">
                  <ArrowUp v-if="expandedSections.qtyInHand" />
                  <ArrowDown v-else />
                </el-icon>
              </div>
            </div>
            <div v-show="expandedSections.qtyInHand" class="overflow-x-auto" style="max-height: 200px; overflow-y: auto;">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50 sticky top-0">
                  <tr>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('dateTime') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('qty') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('aveUnitPrice') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('totalPrice') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('addedBy') }}</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(row, i) in data?.qtyInHand" :key="i">
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ formatPostgresDate(row.createdAt) }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ row.qty }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ formatMoney(row.averageUnitPrice, $ctx.currency) }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ formatMoney(row.totalPrice, $ctx.currency) }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ row.userName || '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Receiving History Table -->
          <div class="bg-white rounded-md shadow-sm border overflow-hidden">
            <div class="px-4 py-3 border-b cursor-pointer hover:bg-gray-50" @click="toggleSection('receivingHistory')">
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-medium text-gray-900">{{ t('receivingHistory') }}</h3>
                <el-icon class="text-gray-500">
                  <ArrowUp v-if="expandedSections.receivingHistory" />
                  <ArrowDown v-else />
                </el-icon>
              </div>
            </div>
            <div v-show="expandedSections.receivingHistory" class="overflow-x-auto" style="max-height: 200px; overflow-y: auto;">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50 sticky top-0">
                  <tr>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('dateTime') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('qty') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('aveUnitPrice') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('totalPrice') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('receivedBy') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('vendor') }}</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(row, i) in data?.receivingHistory" :key="i">
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ formatPostgresDate(row.createdAt) }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ row.qty }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ formatMoney(row.averageUnitPrice, $ctx.currency) || '-' }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ formatMoney(row.totalPrice, $ctx.currency) }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ row.userName || '-' }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ row.vendorName || '-' }}</td>
                  </tr>
                  <tr v-if="!data?.receivingHistory?.length">
                    <td colspan="6" class="px-4 py-2 text-center text-gray-500">{{ t('noData') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Sales History Table -->
          <div class="bg-white rounded-md shadow-sm border overflow-hidden">
            <div class="px-4 py-3 border-b cursor-pointer hover:bg-gray-50" @click="toggleSection('salesHistory')">
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-medium text-gray-900">{{ t('salesHistory') }}</h3>
                <el-icon class="text-gray-500">
                  <ArrowUp v-if="expandedSections.salesHistory" />
                  <ArrowDown v-else />
                </el-icon>
              </div>
            </div>
            <div v-show="expandedSections.salesHistory" class="overflow-x-auto" style="max-height: 200px; overflow-y: auto;">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50 sticky top-0">
                  <tr>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('dateTime') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('qty') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('unitPrice') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('totalPrice') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('cashier') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('salesRep') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('customer') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('city') }}</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(row, i) in data?.salesHistory" :key="i">
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ formatPostgresDate(row.createdAt) }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ row.qty }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ formatMoney(row.averageUnitPrice, $ctx.currency) }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ formatMoney(row.totalPrice, $ctx.currency) }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ row.userName || '-' }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ row.salesRepName || '-' }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ row.customerName || 'Cash Sale' }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ row.customerCity || '-' }}</td>
                  </tr>
                  <tr v-if="!data?.salesHistory?.length">
                    <td colspan="8" class="px-4 py-2 text-center text-gray-500">{{ t('noData') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Transfer History Table -->
          <div class="bg-white rounded-md shadow-sm border overflow-hidden">
            <div class="px-4 py-3 border-b cursor-pointer hover:bg-gray-50" @click="toggleSection('transferHistory')">
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-medium text-gray-900">{{ t('transferHistory') }}</h3>
                <el-icon class="text-gray-500">
                  <ArrowUp v-if="expandedSections.transferHistory" />
                  <ArrowDown v-else />
                </el-icon>
              </div>
            </div>
            <div v-show="expandedSections.transferHistory" class="overflow-x-auto" style="max-height: 200px; overflow-y: auto;">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50 sticky top-0">
                  <tr>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('dateTime') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('qty') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('fromLocation') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('toLocation') }}</th>
                    <th class="px-4 py-2 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('by') }}</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(row, i) in data?.batchTransfers" :key="i">
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ formatPostgresDate(row.createdAt) }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ row.qty }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ row.fromLocationName || '-' }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ row.toLocationName || '-' }}</td>
                    <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ row.userName || '-' }}</td>
                  </tr>
                  <tr v-if="!data?.batchTransfers?.length">
                    <td colspan="5" class="px-4 py-2 text-center text-gray-500">{{ t('noData') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHTTP } from '@/plugins/http'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import { formatPostgresDate } from '@/utils/date'
import { ArrowDown, ArrowUp } from '@element-plus/icons-vue'
import { formatMoney } from '@/utils/money'
import Customer from '@/models/customer/Customer'
import User from '@/models/user/User'
import { useSettingsStore } from '@/stores/settingsStore'

interface Suggestion {
  value: string
  id?: string
}

interface SuggestionResult {
  value: string
  id?: string
}

// i18n
const { t } = useI18n({
  messages: {
    en: {
      batchTrackerReport: 'Batch Tracker Report',
      item: 'Item',
      batch: 'Batch',
      customer: 'Customer',
      selectItemPlaceholder: 'Select an item',
      selectBatchPlaceholder: 'Select batch number',
      selectCustomerPlaceholder: 'Select customer',
      selectCashierPlaceholder: 'Select cashier',
      selectSalesRepPlaceholder: 'Select sales rep',
      selectCityPlaceholder: 'Enter city',
      currentStock: 'Current Stock',
      totalSold: 'Total Sold',
      totalSales: 'Total Sales',
      averageCost: 'Average Cost',
      averageSalePrice: 'Average Sale Price',
      qtyInHand: 'Qty In Hand',
      receivingHistory: 'Receiving History',
      salesHistory: 'Sales History',
      transferHistory: 'Transfer History',
      dateTime: 'Date & Time',
      qty: 'Qty',
      aveUnitPrice: 'Ave Unit Price',
      unitPrice: 'Unit Price',
      totalPrice: 'Total Price',
      addedBy: 'Added by',
      receivedBy: 'Received by',
      cashier: 'Cashier',
      salesRep: 'Sales Rep',
      city: 'City',
      vendor: 'Vendor',
      fromLocation: 'From Location',
      toLocation: 'To Location',
      by: 'By',
      noData: 'No Data Available',
      noExpiry: 'No Expiry'
    },
    ar: {
      batchTrackerReport: 'تقرير تتبع الوجبات',
      item: 'المادة',
      batch: 'الوجبة',
      customer: 'العميل',
      selectItemPlaceholder: 'اختر مادة',
      selectBatchPlaceholder: 'اختر رقم الوجبة',
      selectCustomerPlaceholder: 'اختر عميل',
      selectCashierPlaceholder: 'اختر الكاشير',
      selectSalesRepPlaceholder: 'اختر مندوب المبيعات',
      selectCityPlaceholder: 'أدخل المدينة',
      currentStock: 'المخزون الحالي',
      totalSold: 'إجمالي المباع',
      totalSales: 'إجمالي المبيعات',
      averageCost: 'متوسط التكلفة',
      averageSalePrice: 'متوسط سعر البيع',
      qtyInHand: 'الكمية في المخزن',
      receivingHistory: 'حركات الاستلام',
      salesHistory: 'حركات المبيعات',
      transferHistory: 'حركات التحويل المخزني',
      dateTime: 'التاريخ والوقت',
      qty: 'الكمية',
      aveUnitPrice: 'متوسط سعر الوحدة',
      unitPrice: 'سعر الوحدة',
      totalPrice: 'السعر الإجمالي',
      addedBy: 'أضيف بواسطة',
      receivedBy: 'استلم بواسطة',
      cashier: 'الكاشير',
      salesRep: 'مندوب المبيعات',
      city: 'المدينة',
      vendor: 'المورّد',
      fromLocation: 'من الموقع',
      toLocation: 'إلى الموقع',
      by: 'بواسطة',
      noData: 'لا توجد بيانات',
      noExpiry: 'بلا انتهاء'
    }
  }
})

// Data
const rtable = ref<any>()
const items = ref<Array<any>>([])
const batchNumbers = ref<Array<any>>([])
const customers = ref<Customer[]>([])
const cashiers = ref<Array<any>>([])
const salesReps = ref<Array<any>>([])
const cities = ref<SuggestionResult[]>([])
const selectedItem = ref<any>()
const selectedBatch = ref<any>()
const selectedCustomer = ref<Customer | null>()
const selectedCashier = ref<any>()
const selectedSalesRep = ref<any>()
const selectedCity = ref<string>('')
const data = ref<any>()

// Expand/collapse states for tables
const expandedSections = ref({
  receivingHistory: false,
  salesHistory: false,
  transferHistory: false,
  qtyInHand: false
})

const http = useHTTP()

onMounted(async () => {
  rtable.value.selectedPeriod = 'this_month'
  rtable.value.payload.period = 'this_month'

  // Load initial data for better UX
  await searchCustomers('')
  await searchCashiers('')
  await searchCities('')
  // Sales reps will load on search
})

// Watch for location changes and reset item selection
watch(
  () => rtable.value?.payload?.location_id,
  (newLocationId, oldLocationId) => {
    if (newLocationId !== oldLocationId && oldLocationId !== undefined) {
      // Reset all selections when location changes
      selectedItem.value = null
      selectedBatch.value = null
      selectedCustomer.value = null
      selectedCashier.value = null
      selectedSalesRep.value = null
      selectedCity.value = ''
      items.value = []
      batchNumbers.value = []
      customers.value = []
      cashiers.value = []
      salesReps.value = []
      cities.value = []
    }
  }
)

// Watch for period changes and reload data if item and batch are selected
watch(
  () => rtable.value?.payload?.period,
  (newPeriod, oldPeriod) => {
    if (newPeriod !== oldPeriod && oldPeriod !== undefined) {
      // Reload data if we have item and batch selected
      if (selectedItem.value && selectedBatch.value) {
        updateReportData()
      }
    }
  }
)

// Watch for date range changes and reload data if item and batch are selected
watch(
  () => [rtable.value?.payload?.from_date, rtable.value?.payload?.to_date],
  ([newFromDate, newToDate], [oldFromDate, oldToDate]) => {
    if ((newFromDate !== oldFromDate || newToDate !== oldToDate) && 
        (oldFromDate !== undefined || oldToDate !== undefined)) {
      // Reload data if we have item and batch selected
      if (selectedItem.value && selectedBatch.value) {
        updateReportData()
      }
    }
  }
)

function onDataLoaded(resp: any) {
  data.value = resp
}

function onSelectedItemChanged() {
  selectedBatch.value = null
  batchNumbers.value = []
  if (selectedItem.value && selectedItem.value.itemStocks) {
    // Extract batch numbers from itemStocks
    const batches = selectedItem.value.itemStocks
      .filter((stock: any) => stock.extraProperties?.batchNumber)
      .map((stock: any) => ({
        id: stock.id,
        batchNumber: stock.extraProperties.batchNumber,
        expiryDate: stock.extraProperties.expiryDate,
        itemStockId: stock.id
      }))
    
    // Remove duplicates based on batch number
    const uniqueBatches = batches.filter((batch: any, index: number, self: any[]) => 
      index === self.findIndex((b: any) => b.batchNumber === batch.batchNumber)
    )
    
    batchNumbers.value = uniqueBatches
  }
  updateReportData()
}

function onSelectedBatchChanged() {
  updateReportData()
}

function onSelectedCustomerChanged() {
  updateReportData()
}

function updateReportData() {
  if (selectedItem.value && selectedBatch.value) {
    rtable.value.payload.item_stock_id = selectedBatch.value.itemStockId || selectedBatch.value.id
    rtable.value.payload.batch_number = selectedBatch.value.batchNumber

    // Handle optional filters
    if (selectedCustomer.value) {
      rtable.value.payload.customer_id = selectedCustomer.value.id
    } else {
      delete rtable.value.payload.customer_id
    }

    if (selectedCashier.value) {
      rtable.value.payload.cashier_id = selectedCashier.value.id
    } else {
      delete rtable.value.payload.cashier_id
    }

    if (selectedSalesRep.value) {
      rtable.value.payload.sales_rep_id = selectedSalesRep.value.id
    } else {
      delete rtable.value.payload.sales_rep_id
    }

    if (selectedCity.value) {
      rtable.value.payload.city = selectedCity.value
    } else {
      delete rtable.value.payload.city
    }

    rtable.value.navigate(rtable.value.payload)
  }
}

async function searchItems(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const params = new URLSearchParams()
    params.set('q', query)
    
    // Add location_id if selected
    if (rtable.value?.payload?.location_id) {
      params.set('location_id', rtable.value.payload.location_id)
    }
    
    const url = `reports/inventory/item-batch-lookup?${params.toString()}`
    const itemsRes = await http.get<any[]>(url)
    items.value = itemsRes
  } catch (error) {
    console.log(error)
  }
}



async function searchCustomers(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${Customer.ENDPOINT}?q=${query}`
    const customersRes = await http.get<Customer[]>(url)
    customers.value = customersRes.map((customer) => Customer.from(customer))
  } catch (error) {
    console.log(error)
    customers.value = []
  }
}

async function searchCashiers(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const usersRes = await http.get<any[]>(User.ENDPOINT+'?q='+query)
    cashiers.value = usersRes
  } catch (error) {
    console.log(error)
    cashiers.value = []
  }
}

async function searchSalesReps(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${User.ENDPOINT}/sales-reps`
    const usersRes = await http.get<any[]>(url)
    salesReps.value = usersRes
  } catch (error) {
    console.log(error)
    salesReps.value = []
  }
}

async function searchCities(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const response = await http.get<Suggestion[]>(`/helpers/suggestions?model=customers&key=city&query=${query}`)
    const result: SuggestionResult[] = response.map((r: Suggestion) => {
      return {
        value: r.value,
        id: r.id
      }
    })

    const settingsStore = useSettingsStore()
    settingsStore.cities.forEach(city => {
      if (!response.some((r: Suggestion) => r.value === city.arLabel)) {
        result.push({ value: city.arLabel } as SuggestionResult)
      }
    })

    cities.value = result
  } catch (e) {
    console.log(e)
    cities.value = []
  }
}

function onSelectedCashierChanged() {
  updateReportData()
}

function onSelectedSalesRepChanged() {
  updateReportData()
}

function onSelectedCityChanged() {
  updateReportData()
}

function toggleSection(section: 'receivingHistory' | 'salesHistory' | 'transferHistory' | 'qtyInHand') {
  expandedSections.value[section] = !expandedSections.value[section]
}

function formatExpiryDate(dateString: string) {
  if (!dateString) return t('noExpiry')
  return formatPostgresDate(dateString, 'DD/MM/YYYY')
}
</script>

<style scoped>
</style> 